/**
 * assistant.ts — Cliente API do Atendente IA.
 *
 * Cobre:
 * - streamChat   — POST .../assistant/chat com consumo SSE via fetch + ReadableStream
 * - listConversations — GET .../assistant/conversations
 * - getConversation   — GET .../assistant/conversations/{id}
 *
 * SSE com POST body:
 *   O EventSource do browser só aceita GET. Como o backend exige POST com body,
 *   usamos fetch + response.body.getReader() + TextDecoder para consumo incremental
 *   do stream text/event-stream, parseando linhas "data: {json}\n\n" manualmente.
 *   Headers de auth (Bearer token) e de contexto (x-consoleai-*) são extraídos
 *   do mesmo localStorage / mecanismo que o interceptor axios do client.ts usa,
 *   garantindo consistência sem duplicar lógica.
 *
 * Para listConversations/getConversation, reutilizamos o client axios (api.get).
 */
import { api } from '@/api/client'
import { useAuthStore } from '@/store/auth'
import type { AskAttachmentRef } from '@/api/chat'

// ---------------------------------------------------------------------------
// Tipos dos eventos SSE
// ---------------------------------------------------------------------------

/** Evento inicial que informa o id da conversa (nova ou existente). */
export interface AssistantConversationEvent {
  type: 'conversation'
  id: number
  /** /conversations/{cid}/ask/stream também envia estes (redundante com `id`). */
  conversation_id?: number
  human_message_id?: number
}

/** Evento emitido quando o modelo invocou uma ferramenta interna. */
export interface AssistantToolEvent {
  type: 'tool'
  name: string
  /** Quem deu o passo (consoleai#24893): ausente nos streams antigos = @Atendente; um
   *  especialista (`devops`, `dba`) nos passos de dentro da porta dele. Texto livre na
   *  borda: quem decide se é um especialista conhecido é `passosAoVivo.aplicarEventoDePasso`. */
  agente?: string
}

/** Um especialista terminou a consulta que o @Atendente pediu (`consultar_devops`/`consultar_dba`). */
export interface AssistantToolFimEvent {
  type: 'tool_fim'
  name: string
  agente?: string
}

/** Evento com o texto final da resposta do modelo. */
export interface AssistantTextEvent {
  type: 'text'
  text: string
}

/** Evento que sinaliza o fim do stream. */
export interface AssistantDoneEvent {
  type: 'done'
  bot_message_id?: number
}

/** Evento de erro (ex.: sem acesso à conversa) emitido pelo /ask/stream. */
export interface AssistantErrorEvent {
  type: 'error'
  detail?: string
}

/** União discriminada de todos os eventos SSE do chat. */
export type AssistantChatEvent =
  | AssistantConversationEvent
  | AssistantToolEvent
  | AssistantToolFimEvent
  | AssistantErrorEvent
  | AssistantTextEvent
  | AssistantDoneEvent

// ---------------------------------------------------------------------------
// Tipos das conversas
// ---------------------------------------------------------------------------

export interface AssistantConversation {
  id: number
  title: string
  group_id: number | null
  updated_at: string
}

/** Grupo (pasta) que agrupa conversas relacionadas. */
export interface AssistantGroup {
  id: number
  name: string
  updated_at: string
}

export interface AssistantGroupsResponse {
  items: AssistantGroup[]
}

export interface AssistantMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  created_at: string
  /** O que a busca da KB entregou no turno (TASK-11826): só `document_id` e página. */
  fontes?: { document_id: number; pagina: number | null }[]
  /** Pares antes do corte a 40; só vem quando cortou. */
  fontes_total?: number
}

export interface AssistantConversationDetail extends AssistantConversation {
  messages: AssistantMessage[]
}

export interface AssistantConversationsResponse {
  items: AssistantConversation[]
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

/**
 * Monta os headers de autenticação e contexto exatamente como o interceptor
 * do axios (client.ts) faria. Assim, o fetch do streamChat envia os mesmos
 * cabeçalhos sem depender do axios (que não suporta streaming).
 *
 * O tenantId explícito sobrescreve o selectedTenantId do store — garantindo
 * que o chat sempre usa o tenant correto informado pelo componente pai,
 * independente do tenant globalmente selecionado.
 */
function _buildAuthHeaders(overrideTenantId?: number): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Bearer token: mesma fonte que o interceptor axios usa.
  const token = localStorage.getItem('session_token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Lê contexto do Zustand store (mesma fonte de verdade que o interceptor
  // do axios via registerAuthContextProvider → _getAuthContext).
  // A-07 (TASK-2822): não enviamos mais x-consoleai-user-id/tenant-id — o
  // backend deriva identidade/tenant da sessão. Mantemos apenas o org-id de
  // escopo (per-org-template §3).
  const { selectedOrgId } = useAuthStore.getState()

  if (selectedOrgId != null) {
    headers['x-consoleai-org-id'] = String(selectedOrgId)
  }

  return headers
}

/**
 * Parseia um buffer de texto acumulado do stream SSE e retorna
 * os eventos completos encontrados + o restante não processado.
 *
 * Formato esperado do backend:
 *   data: {"type":"tool","name":"..."}\n\n
 *   data: {"type":"text","text":"..."}\n\n
 *   data: {"type":"done"}\n\n
 */
function _parseSseBuffer(
  buffer: string,
  onEvent: (ev: AssistantChatEvent) => void,
): string {
  // Separa blocos de evento pelo delimitador \n\n
  const blocks = buffer.split('\n\n')
  // O último elemento pode estar incompleto — mantemos no buffer
  const remaining = blocks.pop() ?? ''

  for (const block of blocks) {
    for (const line of block.split('\n')) {
      if (!line.startsWith('data:')) continue
      const jsonStr = line.slice('data:'.length).trim()
      if (!jsonStr) continue
      try {
        const ev = JSON.parse(jsonStr) as AssistantChatEvent
        onEvent(ev)
      } catch {
        // Linha malformada — ignorar silenciosamente
      }
    }
  }

  return remaining
}

// ---------------------------------------------------------------------------
// API pública
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Reconexão com backoff — sobrevive ao rolling deploy / reload do nginx
// ---------------------------------------------------------------------------

/**
 * Sequência de delays (ms) entre tentativas de reconexão.
 * Após esgotadas, o erro da última tentativa é propagado ao chamador.
 */
const SSE_RETRY_DELAYS_MS = [1000, 2000, 5000] as const

/**
 * Decide se um erro é de rede/transiente (elegível para retentativa)
 * ou é um erro de aplicação (HTTP 4xx/5xx, abort) que não deve ser refeito.
 *
 * - TypeError("Failed to fetch" | "network error") → rede caiu → retenta
 * - DOMException com name="AbortError" → cancelamento voluntário → NÃO retenta
 * - Error com mensagem de status HTTP ("… 4xx:…" / "… 5xx:…") → NÃO retenta
 * - Stream encerrou sem "done" (nenhuma exceção) → considerado queda → retenta
 */
function isRetryableError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return false
  if (err instanceof Error) {
    // Erros de status HTTP lançados pelo streamChat contêm o código no texto
    if (/assistant\/chat\s+\d{3}:/.test(err.message)) return false
    // TypeError de rede é retriável
    if (err instanceof TypeError) return true
  }
  // Qualquer outro erro desconhecido → não retenta (conservador)
  return false
}

/** Espera `ms` milissegundos respeitando AbortSignal. */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const id = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    })
  })
}

/**
 * Envia uma mensagem para o Atendente IA com reconexão automática.
 *
 * Comportamento em caso de queda do stream (erro de rede ou encerramento
 * sem evento "done" — p. ex., nginx recriado num rolling deploy):
 *
 * 1. Aguarda delay crescente (1 s / 2 s / 5 s).
 * 2. Reabre o POST, passando o mesmo `conversationId` (ou o id recebido
 *    via evento SSE "conversation" durante a tentativa que caiu), garantindo
 *    continuidade da conversa no backend.
 * 3. Após MAX_RETRIES = 3 retentativas malsucedidas, propaga o último erro.
 *
 * Não retenta em:
 *  - AbortError (cancelamento do usuário via AbortController)
 *  - Erros HTTP 4xx/5xx (falha da aplicação, não de rede)
 *
 * IMPORTANTE (integridade da bolha do assistant): a cada RE-tentativa o
 * backend reabre o turno e re-streama a resposta COMPLETA (não retoma de onde
 * parou). Por isso, antes de cada reabertura, `onRetry` é chamado para o
 * consumidor LIMPAR o conteúdo parcial já recebido da tentativa que caiu —
 * caso contrário o texto da nova tentativa CONCATENARIA com o parcial antigo,
 * produzindo uma resposta duplicada/embaralhada.
 *
 * @param tenantId       Tenant de contexto.
 * @param message        Mensagem do usuário.
 * @param conversationId ID da conversa existente (null = nova conversa).
 * @param onEvent        Callback chamado para cada evento SSE recebido.
 * @param signal         AbortSignal opcional para cancelar o fetch.
 * @param onRetry        Callback opcional disparado imediatamente antes de
 *                       cada reabertura do stream (reset da bolha parcial).
 */
export async function streamChatWithRetry(
  tenantId: number,
  message: string,
  conversationId: number | null,
  onEvent: (ev: AssistantChatEvent) => void,
  signal?: AbortSignal,
  onRetry?: () => void,
): Promise<void> {
  // Mantém o conversation_id mais recente: pode ser atualizado pelo evento
  // SSE "conversation" emitido pelo backend na primeira resposta de uma
  // conversa nova, e deve ser preservado em todas as retentativas.
  let activeConversationId: number | null = conversationId

  // Wraps onEvent para interceptar o id da conversa e rastrear "done"
  let receivedDone = false

  function wrappedOnEvent(ev: AssistantChatEvent): void {
    if (ev.type === 'conversation') {
      activeConversationId = ev.id
    } else if (ev.type === 'done') {
      receivedDone = true
    }
    onEvent(ev)
  }

  // Tentativa inicial
  let lastError: unknown
  try {
    receivedDone = false
    await streamChat(tenantId, message, activeConversationId, wrappedOnEvent, signal)
    if (receivedDone) return // stream completou normalmente
    // Stream encerrou sem "done" — trata como queda transiente
    lastError = new TypeError('SSE stream encerrou sem evento done')
  } catch (err: unknown) {
    if (!isRetryableError(err)) throw err
    lastError = err
  }

  // Retentativas com backoff
  for (const delayMs of SSE_RETRY_DELAYS_MS) {
    await delay(delayMs, signal)

    // Sinaliza ao consumidor para descartar o conteúdo parcial da tentativa
    // anterior ANTES de reabrir o stream: a nova tentativa traz a resposta
    // completa, então não pode concatenar com o parcial que caiu.
    onRetry?.()

    try {
      receivedDone = false
      await streamChat(tenantId, message, activeConversationId, wrappedOnEvent, signal)
      if (receivedDone) return
      lastError = new TypeError('SSE stream encerrou sem evento done')
    } catch (err: unknown) {
      if (!isRetryableError(err)) throw err
      lastError = err
    }
  }

  throw lastError
}

/**
 * Envia uma mensagem para o Atendente IA e consome a resposta SSE.
 *
 * @param tenantId       Tenant de contexto.
 * @param message        Mensagem do usuário.
 * @param conversationId ID da conversa existente (null = nova conversa).
 * @param onEvent        Callback chamado para cada evento SSE recebido.
 * @param signal         AbortSignal opcional para cancelar o fetch.
 */
export async function streamChat(
  tenantId: number,
  message: string,
  conversationId: number | null,
  onEvent: (ev: AssistantChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const url = `/api/v1/tenants/${tenantId}/assistant/chat`
  const body = JSON.stringify({
    message,
    conversation_id: conversationId ?? undefined,
  })

  const response = await fetch(url, {
    method: 'POST',
    headers: _buildAuthHeaders(tenantId),
    body,
    signal,
    credentials: 'include',
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(`assistant/chat ${response.status}: ${text}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Resposta do chat não suporta streaming (body nulo).')
  }

  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      buffer = _parseSseBuffer(buffer, onEvent)
    }

    // Flush final (stream encerrou) — processa o que sobrou no buffer
    buffer += decoder.decode(undefined, { stream: false })
    if (buffer.trim()) {
      _parseSseBuffer(buffer, onEvent)
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Stream encerrado sem o evento `done` — assinatura de corte de proxy/infra, não
 * de falha da aplicação.
 *
 * O turno pode ter funcionado: desde o !1061 a resposta é persistida pela thread
 * que a produziu, e não pelo gerador SSE. Quem trata decide **relendo** a conversa
 * (por isso `humanMessageId`), nunca repetindo o POST — a pergunta humana é
 * commitada cedo de propósito, e repetir duplicaria.
 *
 * Ver docs/STREAMING_SSE_SPEC.md (INV-5, RF-007).
 */
export class SseStreamCutError extends Error {
  constructor(readonly humanMessageId?: number) {
    super('stream do ask/stream encerrou sem evento done')
    this.name = 'SseStreamCutError'
  }
}

/**
 * Consome o /conversations/{cid}/ask/stream (SSE) do @Atendente ancorado numa
 * conversa (mesmo bot rico do /ask: contexto de alerta + propostas de ação).
 * Emite eventos `conversation` → `tool`* → `text` → `done` (ou `error`).
 *
 * Sem auto-retry (o endpoint persiste a pergunta humana cedo; um retry criaria
 * pergunta duplicada). Em queda, lança `SseStreamCutError` — o chamador relê a
 * conversa e só reporta erro se a resposta realmente não estiver lá.
 */
export async function streamAskInConversation(
  cid: number,
  text: string,
  opts: {
    alertMessageId?: number
    shared?: boolean
    attachments?: AskAttachmentRef[]
    /** "Perguntar ao @DBA" do editor: só o id da linha do histórico; o servidor relê o resto. */
    consultaSqlId?: number
  } | undefined,
  onEvent: (ev: AssistantChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`/api/v1/conversations/${cid}/ask/stream`, {
    method: 'POST',
    headers: _buildAuthHeaders(),
    body: JSON.stringify({
      text,
      alert_message_id: opts?.alertMessageId,
      ...(opts?.consultaSqlId ? { consulta_sql_id: opts.consultaSqlId } : {}),
      shared: opts?.shared,
      // TASK-11729: sem isto o anexo da DM morria aqui — subia ao S3 e o
      // backend nem sabia que existia.
      ...(opts?.attachments?.length ? { attachments: opts.attachments } : {}),
    }),
    signal,
    credentials: 'include',
  })

  if (!response.ok) {
    const t = await response.text().catch(() => '')
    throw new Error(`ask/stream ${response.status}: ${t}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Resposta do chat não suporta streaming (body nulo).')
  }

  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  // Rastreia os dois sinais que distinguem corte de sucesso: se o `done` chegou, e
  // qual pergunta este turno criou (para o chamador poder reler em vez de repetir).
  let humanMessageId: number | undefined
  let recebeuDone = false
  const rastreando = (ev: AssistantChatEvent) => {
    if (ev.type === 'conversation') humanMessageId = ev.human_message_id
    else if (ev.type === 'done') recebeuDone = true
    onEvent(ev)
  }

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      buffer = _parseSseBuffer(buffer, rastreando)
    }
    buffer += decoder.decode(undefined, { stream: false })
    if (buffer.trim()) _parseSseBuffer(buffer, rastreando)
  } catch (err) {
    // Queda de rede no meio da leitura é a mesma situação de encerrar sem `done`:
    // o servidor pode ter concluído e persistido o turno. Erro de aplicação não
    // passa por aqui — o status HTTP já foi tratado acima, antes de ler o corpo.
    throw new SseStreamCutError(humanMessageId)
  } finally {
    reader.releaseLock()
  }

  if (!recebeuDone) throw new SseStreamCutError(humanMessageId)
}

/**
 * Retorna as conversas do usuário autenticado no tenant,
 * ordenadas por updated_at desc (máximo 100).
 */
export async function listConversations(
  tenantId: number,
): Promise<AssistantConversationsResponse> {
  const { data } = await api.get<AssistantConversationsResponse>(
    `/v1/tenants/${tenantId}/assistant/conversations`,
  )
  return data
}

/**
 * Retorna o detalhe de uma conversa com suas mensagens.
 */
export async function getConversation(
  tenantId: number,
  conversationId: number,
): Promise<AssistantConversationDetail> {
  const { data } = await api.get<AssistantConversationDetail>(
    `/v1/tenants/${tenantId}/assistant/conversations/${conversationId}`,
  )
  return data
}

/** Exclui uma conversa (e suas mensagens). */
export async function deleteConversation(
  tenantId: number,
  conversationId: number,
): Promise<void> {
  await api.delete(`/v1/tenants/${tenantId}/assistant/conversations/${conversationId}`)
}

/** Renomeia e/ou move uma conversa para um grupo (ou remove do grupo). */
export async function updateConversation(
  tenantId: number,
  conversationId: number,
  patch: { title?: string; group_id?: number; set_group_null?: boolean },
): Promise<void> {
  await api.patch(
    `/v1/tenants/${tenantId}/assistant/conversations/${conversationId}`,
    patch,
  )
}

/** Lista os grupos (pastas) do usuário no tenant. */
export async function listGroups(tenantId: number): Promise<AssistantGroupsResponse> {
  const { data } = await api.get<AssistantGroupsResponse>(
    `/v1/tenants/${tenantId}/assistant/groups`,
  )
  return data
}

/** Cria um grupo (pasta). */
export async function createGroup(
  tenantId: number,
  name: string,
): Promise<AssistantGroup> {
  const { data } = await api.post<AssistantGroup>(
    `/v1/tenants/${tenantId}/assistant/groups`,
    { name },
  )
  return data
}

/** Renomeia um grupo. */
export async function renameGroup(
  tenantId: number,
  groupId: number,
  name: string,
): Promise<void> {
  await api.patch(`/v1/tenants/${tenantId}/assistant/groups/${groupId}`, { name })
}

/** Exclui um grupo E todas as conversas dentro dele. */
export async function deleteGroup(tenantId: number, groupId: number): Promise<void> {
  await api.delete(`/v1/tenants/${tenantId}/assistant/groups/${groupId}`)
}
